import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Link2, Users, Copy, QrCode, Filter } from 'lucide-react';
import apiService from '../utils/apiService';

const SubPartnerLinks = () => {
  const { isDarkMode } = useTheme();
  useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [links] = useState([]);
  const campaignsApiCalled = useRef(false);
  const categoriesApiCalled = useRef(false);

  const sizes = ['All Sizes'];

  useEffect(() => {
    setIsLoaded(true);

    if (!campaignsApiCalled.current) {
      campaignsApiCalled.current = true;
      apiService.getSubCampaigns()
        .then((response) => {
          if (response.success && response.data) {
            setCampaigns(Array.isArray(response.data) ? response.data : []);
          }
        })
        .catch((err) => console.error('Failed to fetch campaigns:', err));
    }

    if (!categoriesApiCalled.current) {
      categoriesApiCalled.current = true;
      apiService.getBannerCategories()
        .then((response) => {
          if (response.success && response.data) {
            setCategories(Array.isArray(response.data) ? response.data : []);
          }
        })
        .catch((err) => console.error('Failed to fetch categories:', err));
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You can add a toast notification here
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleShortUrl = (url) => {
    // URL shortening logic here
    console.log('Shortening URL:', url);
    alert('URL shortening feature will be implemented here');
  };

  const handleGenerateQR = (url) => {
    // QR code generation logic here
    console.log('Generating QR code for:', url);
    alert('QR code generation feature will be implemented here');
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      <div className={`p-3 sm:p-4 lg:p-6 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-800 to-red-900 shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg sm:text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Sub Partner Links
                </h2>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Manage sub partner referral links
                </p>
              </div>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '150ms' }}>
            <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border-gray-800' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'} rounded-xl p-4 border shadow-lg mb-6`}>
              <div className="flex items-center space-x-2 mb-4">
                <Filter className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`} />
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filters</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Campaign Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Select Campaign
                  </label>
                  <select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {[{ label: 'All Campaigns', value: '' }, ...campaigns.map((c) => ({
                      label: c.campaignName || c.name || c.label || String(c),
                      value: c.campaignId || c.id || c.value || String(c),
                    }))].map((campaign) => (
                      <option key={campaign.value || campaign.label} value={campaign.value}>
                        {campaign.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {[{ label: 'All Categories', value: '' }, ...categories.map((c) => ({
                      label: c.categoryName || c.name || c.label || String(c),
                      value: c.categoryId || c.id || c.value || String(c),
                    }))].map((category) => (
                      <option key={category.value || category.label} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Size Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Size
                  </label>
                  <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {sizes.map((size) => (
                      <option key={size} value={size === 'All Sizes' ? '' : size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Links Cards */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
            {links.length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${isDarkMode ? 'bg-black border-gray-800 text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
                No sub partner links available
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {links.map((link, index) => (
                <div
                  key={link.id}
                  className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg border shadow-lg overflow-hidden`}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  {/* Card Header */}
                  <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-black border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {link.title}
                    </h3>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-4">
                    {/* HTML Code Section */}
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        HTML Code:
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          readOnly
                          value={link.htmlCode}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-mono border resize-none ${
                            isDarkMode 
                              ? 'bg-black border-gray-700 text-gray-300' 
                              : 'bg-gray-50 border-gray-300 text-gray-700'
                          }`}
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={() => copyToClipboard(link.htmlCode)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-black hover:bg-gray-700 text-gray-300' 
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                          title="Copy HTML Code"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* URL Section */}
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        URL:
                      </label>
                      <div className="flex items-center space-x-2 mb-2">
                        <input
                          type="text"
                          readOnly
                          value={link.url}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-mono border resize-none ${
                            isDarkMode 
                              ? 'bg-black border-gray-700 text-gray-300' 
                              : 'bg-gray-50 border-gray-300 text-gray-700'
                          }`}
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={() => copyToClipboard(link.url)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-black hover:bg-gray-700 text-gray-300' 
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                          title="Copy URL"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleShortUrl(link.url)}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center space-x-1 ${
                            isDarkMode 
                              ? 'bg-black hover:bg-gray-700 text-gray-300' 
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          <Link2 className="h-3 w-3" />
                          <span>Short</span>
                        </button>
                        <button
                          onClick={() => handleGenerateQR(link.url)}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center space-x-1 ${
                            isDarkMode 
                              ? 'bg-black hover:bg-gray-700 text-gray-300' 
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          <QrCode className="h-3 w-3" />
                          <span>QR</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubPartnerLinks;
